import type { BeforeSendFn, CapturedNetworkRequest } from 'posthog-js';
import { dedupeExceptionEvent } from './exceptionCapture';
import { errorTracker } from './errorTracking';
import { attachProductEmail } from './productEventEmail';
import {
  maskCapturedRecordingUrl,
  sanitizePostHogEventUrls,
} from './sanitizeAnalyticsUrl';

/**
 * PostHog browser client options.
 * `capture_pageview: true` records the landing `$pageview` for anonymous
 * visitors (including query UTMs). It is not gated on authentication.
 * posthog-js 1.336 only autocaptures later history changes when this is
 * `'history_change'`, so route changes after the landing hit are captured
 * in `usePageViewTracking`.
 */
export function createPostHogOptions(input: {
  posthogHost: string;
  isCriticalOnlyMode: boolean;
  secureCookie: boolean;
  debug: boolean;
}) {
  const { posthogHost, isCriticalOnlyMode, secureCookie, debug } = input;

  const beforeSend: BeforeSendFn = (event) => {
    try {
      if (!event) {
        return event;
      }
      sanitizePostHogEventUrls(event);
      if (event.event !== '$exception') {
        return attachProductEmail(event);
      }
      const kept = dedupeExceptionEvent({
        event: event.event,
        properties: event.properties as Record<string, unknown> | undefined,
      });
      return kept ? event : null;
    } catch {
      return event;
    }
  };

  return {
    api_host: posthogHost,
    person_profiles: 'identified_only' as const,

    capture_pageview: true as const,
    capture_pageleave: false,

    // Boolean true: full autocapture, including clicks. No URL allowlist.
    autocapture: true as const,
    rageclick: true as const,

    capture_performance: false,
    capture_dead_clicks: false,
    capture_heatmaps: false,

    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-sensitive]',
      recordCrossOriginIframes: false,
      captureCanvas: { recordCanvas: false },
      disable_session_recording: isCriticalOnlyMode,
      // Replay meta hrefs are the page URL. This strips token params there
      // and on captured network request URLs. maskAllInputs is unchanged.
      maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) =>
        maskCapturedRecordingUrl(request),
    },

    secure_cookie: secureCookie,
    mask_all_element_attributes: false,
    mask_all_text: false,

    request_batching: true,
    disable_compression: false,

    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: isCriticalOnlyMode ? false : true,
    },

    before_send: beforeSend,

    rate_limiting: {
      events_per_second: isCriticalOnlyMode ? 5 : 10,
      events_burst_limit: isCriticalOnlyMode ? 50 : 100,
    },

    loaded: (ph: {
      capture: (event: string, properties?: Record<string, unknown>) => void;
      register?: (properties: Record<string, unknown>) => void;
    }) => {
      const appVersion = String(import.meta.env.VITE_MEMARA_RELEASE || '').trim();
      if (appVersion && typeof ph.register === 'function') {
        ph.register({ app_version: appVersion });
      }
      errorTracker.setPostHog(ph);
      if (debug) {
        console.log('PostHog initialized successfully', {
          host: posthogHost,
          hasPosthog: !!ph,
        });
      }
    },

    on_request_error: (response: { statusCode?: number; text?: string }) => {
      if (debug) {
        console.warn(
          'PostHog request failed:',
          response?.statusCode,
          response?.text,
        );
      }
    },
  };
}
